import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { MapPin, Loader2, AlertCircle, Search } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

// Global Google Maps type declaration
declare global {
  interface Window {
    google: typeof google;
    initGooglePlacesAPI?: () => void; // Le callback global pour l'API Places
  }
}

// Drapeaux globaux pour s'assurer que le script Google Places API est chargé une seule fois
let _googlePlacesScriptLoaded = false;
let _googlePlacesApiPromise: Promise<void> | null = null;
export interface LocationDetails {
  name: string;
  city: string;
  country: string;
  continent: string;
  region: string;
  latitude: number;
  longitude: number;
  formatted_address?: string;
}
interface GoogleMapSearchBarProps {
  onLocationSelect: (location: LocationDetails) => void;
  selectedLocation: LocationDetails | null;
  placeholder?: string;
  initialValue?: string;
  // showMap prop n'est plus nécessaire car la carte est retirée
}
const GoogleMapSearchBar: React.FC<GoogleMapSearchBarProps> = ({
  onLocationSelect,
  selectedLocation,
  placeholder = "Rechercher un lieu (ex: Musée du Louvre, Paris)",
  initialValue = ""
}) => {
  const [searchQuery, setSearchQuery] = useState(initialValue);
  const [isLoading, setIsLoading] = useState(true); // Gère le chargement initial de Google Places API
  const [error, setError] = useState<string | null>(null); // Gère les erreurs
  const [suggestions, setSuggestions] = useState<google.maps.places.AutocompletePrediction[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const autocompleteInputRef = useRef<HTMLInputElement>(null);
  const autocompleteServiceRef = useRef<google.maps.places.AutocompleteService | null>(null);
  const placesServiceRef = useRef<google.maps.places.PlacesService | null>(null);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const extractLocationDetails = useCallback((place: google.maps.places.PlaceResult): LocationDetails => {
    console.log('🏗️ Extracting location details from place:', place);
    
    let cityName = "";
    let regionName = "";
    let countryName = "";
    let continentName = "Europe";
    
    if (place.address_components) {
      // First pass: extract all components
      place.address_components.forEach(component => {
        const types = component.types;

        // City extraction with priority order
        if (!cityName) {
          if (types.includes('locality')) {
            cityName = component.long_name;
          } else if (types.includes('postal_town')) {
            cityName = component.long_name;
          } else if (types.includes('administrative_area_level_2')) {
            cityName = component.long_name;
          } else if (types.includes('sublocality')) {
            cityName = component.long_name;
          }
        }

        // Region extraction: prefer administrative_area_level_1 (état/région/préfecture)
        if (types.includes('administrative_area_level_1')) {
          regionName = component.long_name;
        }

        // Country
        if (types.includes('country')) {
          countryName = component.long_name;
          const europeanCountries = ['France', 'Spain', 'Italy', 'Germany', 'United Kingdom', 'Netherlands', 'Belgium', 'Switzerland'];
          const asianCountries = ['Japan', 'China', 'South Korea', 'Thailand', 'Vietnam', 'Singapore'];
          if (europeanCountries.includes(component.long_name)) {
            continentName = 'Europe';
          } else if (asianCountries.includes(component.long_name)) {
            continentName = 'Asia';
          }
        }
      });
      
      // Second pass: fallback for city if still not found (use administrative_area_level_1)
      if (!cityName && regionName) {
        cityName = regionName;
      }
      
      // If city name wasn't found, try to extract from place name or formatted address
      if (!cityName && place.name) {
        // For cases like "Tokyo, Japan" where name is the city
        const nameParts = place.name.split(',');
        if (nameParts.length > 0) {
          cityName = nameParts[0].trim();
        }
      }
    }
    
    // Avoid duplication: if city and region are the same, clear region
    if (cityName && regionName && cityName === regionName) {
      regionName = "";
    }
    
    const locationDetails: LocationDetails = {
      name: place.name || place.formatted_address || "Lieu inconnu",
      city: cityName || "Ville inconnue",
      country: countryName || "Pays inconnu",
      continent: continentName,
      region: regionName,
      latitude: place.geometry?.location?.lat() || 0,
      longitude: place.geometry?.location?.lng() || 0,
      formatted_address: place.formatted_address
    };
    
    console.log('✅ Extracted location details:', locationDetails);
    return locationDetails;
  }, []);

  // --- LOGIQUE DE CHARGEMENT DE L'API GOOGLE PLACES (pour la recherche) ---
  useEffect(() => {
    const loadGooglePlacesAPI = async () => {
      // Vérification ultra-robuste pour éviter le double chargement du script
      if (_googlePlacesScriptLoaded || window.google && window.google.maps && autocompleteServiceRef.current && placesServiceRef.current) {
        console.log('✅ Google Places API already loaded and services initialized. Skipping load.');
        setIsLoading(false);
        // Si déjà chargé, on tente juste d'initialiser les services
        if (window.google) {
          initializeGooglePlacesServices();
        }
        return;
      }

      // Assigner la promesse de chargement pour éviter les appels concurrents
      if (_googlePlacesApiPromise) {
        console.log('Google Places API load already in progress. Waiting...');
        await _googlePlacesApiPromise; // Attendre la fin du chargement en cours
        initializeGooglePlacesServices(); // Tenter d'initialiser après
        return;
      }
      _googlePlacesApiPromise = (async () => {
        try {
          console.log('🔄 Starting Google Places API load process...');
          setIsLoading(true);
          setError(null);

          // Récupérer la clé API Google Maps depuis Supabase Edge Function
          console.log('🔑 Fetching Google Maps API key from Supabase...');
          const {
            data,
            error: apiError
          } = await supabase.functions.invoke('get-google-maps-key', {
            method: 'GET'
          });
          console.log('DEBUG: Raw response from invoke:', {
            data,
            apiError
          });
          let apiKey: string | null = null;
          if (apiError) {
            console.error('❌ Error fetching API key:', apiError);
            throw new Error(`Erreur lors de la récupération de la clé API: ${apiError.message}`);
          }

          // --- CORRECTION CLÉ : EXTRACTION ROBUSTE DE data.key ou data.apiKey ---
          if (data) {
            if (typeof data.key === 'string') {
              // Premier essai: data.key (comme Edge Function le renvoie)
              apiKey = data.key;
              console.log('DEBUG: API Key extracted successfully from data.key');
            } else if (typeof data.apiKey === 'string') {
              // Deuxième essai: data.apiKey (si invoke le renomme)
              apiKey = data.apiKey;
              console.log('DEBUG: Clé trouvée dans data.apiKey (par supabase.invoke)');
            } else if (data.data && typeof data.data.key === 'string') {
              // Troisième essai: data.data.key
              apiKey = data.data.key;
              console.log('DEBUG: Clé trouvée dans data.data.key');
            }
          }
          // --- FIN CORRECTION CLÉ ---

          if (!apiKey) {
            console.error('❌ No API key found in expected format from Supabase. Data object:', data);
            throw new Error('Aucune clé API Google Maps trouvée dans la réponse attendue.');
          }
          console.log('✅ API key retrieved successfully');

          // Charger le script Google Maps (uniquement la bibliothèque 'places')
          console.log('📦 Loading Google Maps script for Places API...');
          const script = document.createElement('script');
          script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places&callback=initGooglePlacesAPI`; // 'places' seulement
          script.async = true;
          script.defer = true;

          // Définir la fonction de callback globale pour Google Places API
          window.initGooglePlacesAPI = () => {
            console.log('🎯 Google Places API callback triggered');
            initializeGooglePlacesServices(); // Initialise les services Place/Autocomplete
          };
          script.onerror = () => {
            console.error('❌ Failed to load Google Places API script');
            setError('Erreur lors du chargement du script Google Places API.');
            setIsLoading(false);
            _googlePlacesScriptLoaded = false;
            _googlePlacesApiPromise = null;
          };
          document.head.appendChild(script);
          _googlePlacesScriptLoaded = true; // Flag pour indiquer que le script a été ajouté
        } catch (err) {
          console.error('❌ Critical Error in loadGooglePlacesAPI:', err);
          setError(err instanceof Error ? err.message : 'Erreur inconnue lors du chargement API.');
          setIsLoading(false);
          _googlePlacesApiPromise = null;
          throw err;
        }
      })();
      _googlePlacesApiPromise.catch(() => {});
    };
    loadGooglePlacesAPI();

    // --- FONCTION DE NETTOYAGE LORS DU DÉMONTAGE DU COMPOSANT ---
    return () => {
      console.log('🧹 GoogleMapSearchBar: Running cleanup effect.');
      const scripts = document.querySelectorAll('script[src^="https://maps.googleapis.com/maps/api/js"]');
      scripts.forEach(script => {
        // Supprime uniquement le script que CE composant a ajouté
        const scriptElement = script as HTMLScriptElement;
        if (scriptElement.src.includes('callback=initGooglePlacesAPI') && scriptElement.parentElement) {
          scriptElement.parentElement.removeChild(scriptElement);
        }
      });
      delete window.initGooglePlacesAPI; // Supprime la fonction de callback globale

      // Nettoie les références aux services Autocomplete et Places
      autocompleteServiceRef.current = null;
      placesServiceRef.current = null;
      console.log('🧹 GoogleMapSearchBar: Google Places API cleanup complete.');
    };
  }, []);

  // --- INITIALISATION DES SERVICES GOOGLE PLACES API (Appelée par le callback) ---
  const initializeGooglePlacesServices = useCallback(() => {
    try {
      console.log('🚀 Initializing Google Places API services (callback)...');
      if (!window.google || !window.google.maps || !window.google.maps.places) {
        console.error('❌ Google Places API not available after callback. This should not happen.');
        setError('API Google Places non disponible après chargement.');
        setIsLoading(false);
        return;
      }
      if (!autocompleteServiceRef.current) {
        autocompleteServiceRef.current = new window.google.maps.places.AutocompleteService();
        console.log('✅ AutocompleteService initialized.');
      }
      if (!placesServiceRef.current) {
        placesServiceRef.current = new window.google.maps.places.PlacesService(document.createElement('div'));
        console.log('✅ PlacesService initialized.');
      }
      setIsLoading(false);
      console.log('🎉 Google Places API initialization complete');
    } catch (err) {
      console.error('❌ Error initializing Google Places services:', err);
      setError('Erreur lors de l\'initialisation de Google Places.');
      setIsLoading(false);
    }
  }, []);

  // --- RECHERCHE AUTOMATIQUE AVEC SUGGESTIONS (avec debounce) ---
  const handleSearchInput = (value: string) => {
    setSearchQuery(value);
    if (!value.trim()) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    debounceTimerRef.current = setTimeout(() => {
      performAutocompleteSearch(value);
    }, 300);
  };
  const performAutocompleteSearch = (value: string) => {
    if (!autocompleteServiceRef.current) {
      console.log('🔍 AutocompleteService not ready. Cannot search.');
      setSuggestions([]);
      setShowSuggestions(false);
      setError('Recherche non disponible: Google Places non chargé.');
      return;
    }
    console.log('🔍 Performing Autocomplete search for:', value);
    const request: google.maps.places.AutocompletionRequest = {
      input: value,
      types: ['establishment', 'geocode'] // Types compatibles
      //componentRestrictions: { country: ['fr', 'be', 'ch', 'lu'] }
    };
    autocompleteServiceRef.current.getPlacePredictions(request, (predictions, status) => {
      console.log('📋 Autocomplete status:', status, 'predictions:', predictions?.length);
      if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
        setSuggestions(predictions.slice(0, 10));
        setShowSuggestions(true);
        setError(null);
      } else {
        console.log('No predictions found or error status:', status);
        setSuggestions([]);
        setShowSuggestions(false);
        setError(`Erreur de recherche: ${status === window.google.maps.places.PlacesServiceStatus.OVER_QUERY_LIMIT ? 'Quota dépassé.' : 'Aucun résultat ou statut invalide.'}`);
      }
    });
  };

  // Sélectionner une suggestion
  const handleSuggestionSelect = async (prediction: google.maps.places.AutocompletePrediction) => {
    if (!prediction.place_id || !placesServiceRef.current) return;
    console.log('🎯 Suggestion selected:', prediction);
    setShowSuggestions(false);
    setSearchQuery(prediction.description);
    try {
      const request: google.maps.places.PlaceDetailsRequest = {
        placeId: prediction.place_id,
        fields: ['place_id', 'geometry', 'name', 'formatted_address', 'address_components', 'types']
      };
      placesServiceRef.current.getDetails(request, (place, status) => {
        console.log('📍 Place details status:', status, 'place:', place);
        if (status === window.google.maps.places.PlacesServiceStatus.OK && place && place.geometry?.location) {
          const locationDetails = extractLocationDetails(place);
          onLocationSelect(locationDetails);
          setError(null);
        } else {
          console.error('❌ Failed to get place details:', status);
          setError(`Impossible d'obtenir les détails du lieu: ${status}`);
        }
      });
    } catch (err) {
      console.error('❌ Error getting place details:', err);
      setError('Erreur lors de la récupération des détails du lieu.');
    }
  };
  useEffect(() => {
    if (selectedLocation) {
      setSearchQuery(selectedLocation.formatted_address || selectedLocation.name || '');
    }
  }, [selectedLocation]);

  // Met à jour le champ de recherche quand initialValue change et lance la recherche automatiquement
  useEffect(() => {
    if (initialValue && !selectedLocation) {
      setSearchQuery(initialValue);
      
      // Attendre que l'API soit prête avant de lancer la recherche
      const attemptSearch = (retries = 0) => {
        const maxRetries = 10;
        const retryDelay = 500; // 500ms entre chaque tentative
        
        if (autocompleteServiceRef.current && !isLoading) {
          // API prête, lancer la recherche
          if (initialValue.trim()) {
            performAutocompleteSearch(initialValue);
          }
        } else if (retries < maxRetries) {
          // API pas encore prête, réessayer
          setTimeout(() => attemptSearch(retries + 1), retryDelay);
        }
      };
      
      // Commencer les tentatives après un court délai initial
      setTimeout(() => attemptSearch(0), 300);
    }
  }, [initialValue, selectedLocation, isLoading]);
  return <div className="space-y-4">
      {/* Barre de recherche */}
      <div className="relative space-y-2">
        
        
        <div className="relative">
          <Input ref={autocompleteInputRef} id="google-search" placeholder={placeholder} value={searchQuery} onChange={e => handleSearchInput(e.target.value)} onFocus={() => suggestions.length > 0 && setShowSuggestions(true)} onBlur={() => setTimeout(() => setShowSuggestions(false), 200)} disabled={isLoading || !autocompleteServiceRef.current} className="w-full pr-10" />
          
          {isLoading && <Loader2 className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 animate-spin text-muted-foreground" />}
        </div>

        {showSuggestions && suggestions.length > 0 && <Card className="absolute top-full left-0 right-0 z-50 mt-1 max-h-60 overflow-y-auto">
            <div className="p-2">
              {suggestions.map((suggestion, index) => <Button key={suggestion.place_id || index} variant="ghost" className="w-full justify-start text-left h-auto p-3 hover:bg-muted/50" onClick={() => handleSuggestionSelect(suggestion)}>
                  <div className="flex items-start gap-2">
                    <MapPin className="w-4 h-4 mt-0.5 text-muted-foreground flex-shrink-0" />
                    <div className="min-w-0 flex-1">
                      <div className="font-medium text-sm truncate">
                        {suggestion.structured_formatting?.main_text || suggestion.description}
                      </div>
                      <div className="text-xs text-muted-foreground truncate">
                        {suggestion.description}
                      </div>
                    </div>
                  </div>
                </Button>)}
            </div>
          </Card>}

        
        {error && <p className="text-sm text-destructive flex items-center gap-1">
            <AlertCircle className="w-4 h-4" />
            {error}
          </p>}
      </div>

      {/* La carte interactive est retirée de ce composant */}
      {/* showMap && ( ... ) */}

      {selectedLocation && <Card className="p-2 bg-muted/30 mt-2">
          <div className="flex items-center gap-2">
            <MapPin className="w-3 h-3 text-primary flex-shrink-0" />
            <div className="min-w-0">
              <h4 className="font-medium text-xs truncate">{selectedLocation.name}</h4>
              <p className="text-xs text-muted-foreground truncate">
                {selectedLocation.city}
                {selectedLocation.region && selectedLocation.region !== selectedLocation.city && `, ${selectedLocation.region}`}
                , {selectedLocation.country}
              </p>
            </div>
          </div>
        </Card>}
    </div>;
};
export default GoogleMapSearchBar;